/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import type { FormEvent, JSX, MouseEvent } from "react"
import { useState } from "react"

import type {
    ApplicationTypeCategory,
    ApplicationTypePlatform
} from "../../features/application-type/application-type-types"
import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import type { Organization } from "../../features/organization/organization-types"
import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { useViewModel } from "../app/app-hooks"
import { Button } from "../ui-widgets/button/button"
import { SelectFilter } from "../ui-widgets/filter/select-filter"

import { ApplicationTypeIconSelectView } from "./application-type-icon-select-view"
import { ApplicationTypePlatformDropdownView } from "./application-type-platform-dropdown-view"

interface ApplicationTypeCreateModalProps {
    organizations: Organization[]
    selectedOrgId: number
    onClose: () => void
    onSuccess: () => void
}

export const ApplicationTypeCreateModal = observer(
    ({ organizations, selectedOrgId, onClose, onSuccess }: ApplicationTypeCreateModalProps): JSX.Element => {
        const vm = useViewModel(ApplicationTypeViewModel)
        const loggerVM = useViewModel(LoggerViewModel)

        const [orgId, setOrgId] = useState<number>(selectedOrgId)
        const [packageName, setPackageName] = useState<string>("")
        const [desc, setDesc] = useState<string>("")
        const [platform, setPlatform] = useState<ApplicationTypePlatform>("ANDROID")
        const [category, setCategory] = useState<ApplicationTypeCategory>("DEV")
        const [iconFile, setIconFile] = useState<File | null>(null)

        // invalid fields state
        const [invalid, setInvalid] = useState<string[]>([])
        const [isSubmitting, setIsSubmitting] = useState(false)

        // Input names - platform is always filled, no need to validate it
        const iPackageName = "packageName"
        const iDesc = "desc"
        const iOrgIdSelect = "orgIdSelect"

        const handleOrgChange = (val: number | null) => {
            if (val === null) {
                setOrgId(-1)
                return
            }
            setOrgId(Number(val))
            removeFromInvalid(iOrgIdSelect)
        }

        const handleSubmit = async (event: FormEvent) => {
            event.stopPropagation()
            event.preventDefault()

            // final trim
            setPackageName(packageName.trim())
            setDesc(desc.trim())

            // validate
            const inv = validate()
            if (inv.length !== 0) {
                setInvalid(inv)
                return
            }

            // The spinner covers every hop: create, icon upload and list refresh.
            setIsSubmitting(true)
            try {
                //submit
                const app = await vm.createAppType(orgId, desc, packageName, platform, category)
                if (!app.success) {
                    // error log is done in application type service
                    return
                }

                // No icon path: we are done here
                if (!iconFile) {
                    await vm.getAllAppTypes()
                    onSuccess()
                    return
                }

                // Icon path
                const appId = app.data?.id
                if (!appId) {
                    loggerVM.logErrorMessage("Application ID missing in application type.")
                    return
                }
                const icon = await vm.updateAppTypeIcon(appId, iconFile)
                if (icon.success) {
                    await vm.getAllAppTypes()
                    onSuccess()
                }
                // error logged in icon service call
            } finally {
                setIsSubmitting(false)
            }
        }

        const validate = (): string[] => {
            const invalid: string[] = []

            if (orgId === -1) {
                invalid.push(iOrgIdSelect)
            }
            if (!packageName) {
                invalid.push(iPackageName)
            }
            if (!desc) {
                invalid.push(iDesc)
            }
            return invalid
        }

        const removeFromInvalid = (iName: string) => {
            const updatedInvalid = invalid.filter((name) => name !== iName)
            setInvalid(updatedInvalid)
        }

        const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
            if (event.target === event.currentTarget) {
                onClose()
            }
        }

        return (
            <div className="modal block modal-on-background" tabIndex={-1} role="dialog" onClick={handleBackdropClick}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header custom-card-header bg-legacy-primary text-white">
                            <h5 className="modal-title">Create Application</h5>
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                aria-label="Close"
                                onClick={onClose}
                            />
                        </div>
                        <div className="modal-body py-legacy">
                            <form onSubmit={handleSubmit} className="grid gap-legacy">
                                <SelectFilter<number>
                                    label="Organization"
                                    noSelectionLabel="Select organization..."
                                    options={organizations.map((org) => ({
                                        label: org.name,
                                        value: org.id
                                    }))}
                                    value={orgId}
                                    onChange={handleOrgChange}
                                    disabled={organizations.length === 1}
                                    selectClassName={invalid.includes(iOrgIdSelect) ? "is-invalid" : ""}
                                />

                                <div className="form-floating">
                                    <input
                                        type="text"
                                        id={iPackageName}
                                        className={`form-control ${invalid.includes(iPackageName) ? "is-invalid" : ""}`}
                                        placeholder="Package Name"
                                        value={packageName}
                                        onChange={(e) => {
                                            setPackageName(e.target.value.trim()) // don't allow leading/trailing spaces in package name
                                            removeFromInvalid(iPackageName)
                                        }}
                                    />
                                    <label htmlFor={iPackageName}>Package Name</label>
                                </div>

                                <div className="form-floating">
                                    <input
                                        type="text"
                                        id={iDesc}
                                        className={`form-control ${invalid.includes(iDesc) ? "is-invalid" : ""}`}
                                        placeholder="Description"
                                        value={desc}
                                        onChange={(e) => {
                                            setDesc(e.target.value)
                                            removeFromInvalid(iDesc)
                                        }}
                                    />
                                    <label htmlFor={iDesc}>Description</label>
                                </div>

                                <ApplicationTypeIconSelectView onIconSelect={setIconFile} />

                                <ApplicationTypePlatformDropdownView
                                    platform={platform}
                                    onPlatformSelected={setPlatform}
                                />

                                {categorySelectDropdown(["DEV", "TEST", "PROD"], category, setCategory)}

                                <div className="modal-footer px-0 pb-0">
                                    <Button variant="secondary" onClick={onClose}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" busy={isSubmitting}>
                                        Create
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
)

function categorySelectDropdown(
    cats: ApplicationTypeCategory[],
    selected: ApplicationTypeCategory,
    onSelect: (category: ApplicationTypeCategory) => void
): JSX.Element {
    return (
        <div className="form-floating">
            <select
                className="form-select"
                id="catSelect"
                value={selected}
                onChange={(evt) => onSelect(evt.target.value as ApplicationTypeCategory)}
            >
                {cats.map((category) => (
                    <option key={category} value={category}>
                        {category}
                    </option>
                ))}
            </select>
            <label htmlFor="catSelect">Category</label>
        </div>
    )
}
